import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, bootTenantHealthCheck } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=authed
  const [checking, setChecking] = useState(true);
  const [demoMode, setDemoMode] = useState(false);
  const [license, setLicense] = useState(null);
  const [e2eMode, setE2eMode] = useState(null); // {enabled, suite} — admin-only
  const [ai, setAi] = useState(null); // {available, canUse, licensed, managed, ...} from /config
  const [wizard, setWizard] = useState(null); // SaaS initial-setup wizard status — admin-only

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // SaaS-only: probe the tenant backend once before the first data call. An unprovisioned or
      // suspended tenant is redirected to the status screen here (page unloads). No-op on self-hosted.
      const verdict = await bootTenantHealthCheck();
      if (verdict === "gone" || verdict === "expired") return; // navigating away
      if (cancelled) return;
      api
        .get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => setUser(false))
        .finally(() => setChecking(false));
      api.get("/auth/setup-status").then((r) => setDemoMode(!!r.data.demoMode)).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshLicense = useCallback(() => {
    api.get("/license").then((r) => setLicense(r.data)).catch(() => {});
  }, []);

  const login = useCallback(async (email, password) => {
    await api.post("/auth/login", { email, password });
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing session locally.", e);
    }
    setUser(false);
  }, []);

  useEffect(() => {
    if (user) refreshLicense();
  }, [user, refreshLicense]);

  useEffect(() => {
    if (user) api.get("/config").then((r) => setAi(r.data.ai || null)).catch(() => setAi(null));
    else setAi(null);
  }, [user]);

  useEffect(() => {
    const isAdminUser = !!user && (user.role === "admin" || user.role === "superadmin");
    if (!isAdminUser) { setE2eMode(null); return; }
    api.get("/e2e/health")
      .then((r) => setE2eMode({ enabled: !!r.data.enabled, suite: r.data.suite }))
      .catch(() => setE2eMode(null));
  }, [user]);

  const refreshWizard = useCallback(() => {
    api.get("/wizard/status").then((r) => setWizard(r.data)).catch(() => setWizard(null));
  }, []);

  useEffect(() => {
    const isAdminUser = !!user && (user.role === "admin" || user.role === "superadmin");
    if (!isAdminUser) { setWizard(null); return; }
    refreshWizard();
  }, [user, refreshWizard]);

  const value = useMemo(() => {
    const perms = (user && user.permissions) || [];
    const isSuper = !!user && user.role === "superadmin";
    const isFullAdmin = !!user && (user.role === "admin" || isSuper);
    const can = (p) => isFullAdmin || (!!user && perms.includes(p));
    return {
      user,
      checking,
      login,
      logout,
      can,
      permissions: perms,
      isAdmin: isFullAdmin,
      isSuperAdmin: isSuper,
      accessMode: (user && user.accessMode) || "normal",
      accessMessage: (user && user.accessMessage) || null,
      accessExpiresAt: (user && user.accessExpiresAt) || null,
      accessPortal: (user && user.accessPortal) || null,
      isManager: isFullAdmin || (!!user && user.role === "manager"),
      isOperator: isFullAdmin || (!!user && ["manager", "user"].includes(user.role)),
      demoMode,
      license,
      e2eMode,
      ai,
      wizard,
      refreshWizard,
      refreshLicense,
      setUser,
    };
  }, [user, checking, login, logout, demoMode, license, e2eMode, ai, wizard, refreshWizard, refreshLicense]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
